import type { ComputedRef, Ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import { isEmpty } from 'lodash-es';
import { computed, ref } from 'vue';

import type { GGRProductFields, IAdminFeesForm, IAdminFeesMerchant, IShowAdminFeesResponse } from '@modules/admin-fees/types';
import type { ICountry } from '@modules/countries/types';
import { AFFILIATE_PROGRAM_ADMIN_FEES_PERMISSIONS } from '@modules/admin-fees/constants';
import { useAdminFeesMerchantsQuery, useShowAdminFeesQuery } from '@modules/admin-fees/queries';
import { isGGRProductField } from '@modules/admin-fees/types';
import { getProductsLocaleObject } from '@modules/admin-fees/utils';
import { useConfig } from '@modules/config/composables';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import { useDevice } from '@modules/core/composables/device';
import { EAasmState, EExpectedAction } from '@modules/core/enums';
import sortByProperty from '@modules/core/utils/sortByProperty';
import { useCountriesQuery } from '@modules/countries/queries';
import { usePermissionsStore } from '@modules/permissions/store';
import { assert, isString } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

export interface IAdminFeesCommonComposable {
  activeFilterMerchantId: Ref<number | undefined>;
  countryFeesSettingsDialog: Ref<boolean>;
  selectedCountryFeesFilterIndex: Ref<number | undefined>;
  formPayload: Ref<IAdminFeesForm>;
  isGreaterThenSm: Ref<boolean>;
  adminFeesData: Ref<IShowAdminFeesResponse | undefined>;
  isShowLoading: Ref<boolean>;
  merchantList: Ref<IAdminFeesMerchant[] | undefined>;
  countriesList: Ref<ICountry[] | undefined>;
  isEdit: ComputedRef<boolean>;
  editFromPermission: ComputedRef<boolean>;
  productsKeyList: ComputedRef<[GGRProductFields, string][]>;
  warnMessage: ComputedRef<string | undefined>;
  onCountryFilterEdit(index: number): void;
  onSelectMerchant(merchantId: number): void;
}

export default function useAdminFeesCommon(): IAdminFeesCommonComposable {
  const activeFilterMerchantId = ref<number>();
  const countryFeesSettingsDialog = ref(false);
  const selectedCountryFeesFilterIndex = ref<number>();
  const formPayload = ref<IAdminFeesForm>({
    date: new Date(),
    admin_fees_merchant_filters: [],
  });

  const isGreaterThenSm = useDevice().breakpoints.sm;
  const { config } = useConfig();
  const { t } = useTranslation();
  const { formatMonthDate } = useFormatMonthDate();
  const { hasPermission } = usePermissionsStore();

  const selectedMonth = computed<string | undefined>(() => formPayload.value?.date ? formatMonthDate(formPayload.value?.date) : undefined);

  const { data: adminFeesData, isLoading: isShowLoading } = useShowAdminFeesQuery(selectedMonth);
  const { data: merchantList } = useAdminFeesMerchantsQuery();
  const { data: countriesList } = useCountriesQuery();

  const isEdit = computed(() => adminFeesData.value?.expected_action === EExpectedAction.UPDATE);

  const editFromPermission = computed(() => isEdit.value
    ? hasPermission(AFFILIATE_PROGRAM_ADMIN_FEES_PERMISSIONS.EDIT).value
    : hasPermission(AFFILIATE_PROGRAM_ADMIN_FEES_PERMISSIONS.CREATE).value,
  );

  const productsKeyList = computed<[GGRProductFields, string][]>(() => {
    const productsObj = getProductsLocaleObject(config.value?.products_list);

    return Object.entries(productsObj).map(([product, shortcode]) => {
      assert(isGGRProductField(product));
      return [product, shortcode];
    });
  });

  const warnMessage = computed<string | undefined>(() => {
    const fromDate = adminFeesData.value?.message?.data?.from_date;
    const toDate = adminFeesData.value?.message?.data?.to_date;

    return isEmpty(adminFeesData.value?.message)
      ? undefined
      : t(
          adminFeesData.value.message?.text,
          {
            replace: {
              ...adminFeesData.value.message.data,
              from_date: isString(fromDate) ? formatMonthDate(new Date(fromDate)) : fromDate,
              to_date: isString(toDate) ? formatMonthDate(new Date(toDate)) : toDate,
            },
          },
        );
  });

  function onCountryFilterEdit(index: number): void {
    selectedCountryFeesFilterIndex.value = index;
    countryFeesSettingsDialog.value = true;
  }

  function onSelectMerchant(merchantId: number): void {
    activeFilterMerchantId.value = merchantId;
  }

  function recalculateFilters(): void {
    if (adminFeesData.value?.history_of_admin_fees_change) {
      formPayload.value = {
        ...adminFeesData.value?.history_of_admin_fees_change,
        date: formPayload.value.date,
        admin_fees_merchant_filters: sortByProperty(
          adminFeesData.value?.history_of_admin_fees_change.admin_fees_merchant_filters.map((item) => ({
            ...item,
            isAllMerchants: merchantList.value?.find(({ id }) => id === item.merchant_id)?.default ?? false,
            admin_fees_country_filters: sortByProperty(
              item.admin_fees_country_filters.map((countryItem, index) => ({
                ...countryItem,
                index,
                isAllCountries: countryItem.aasm_state === EAasmState.DEFAULT,
              })),
              'isAllCountries',
            ),
          })),
          'isAllMerchants',
        ),
      };

      if (!activeFilterMerchantId.value || !formPayload.value?.admin_fees_merchant_filters.map(({ merchant_id }) => merchant_id).includes(activeFilterMerchantId.value)) {
        activeFilterMerchantId.value = formPayload.value?.admin_fees_merchant_filters?.[0].merchant_id;
      }
    }
  }

  watchImmediate(adminFeesData, recalculateFilters);

  return {
    activeFilterMerchantId,
    countryFeesSettingsDialog,
    selectedCountryFeesFilterIndex,
    formPayload,
    isGreaterThenSm,
    adminFeesData,
    isShowLoading,
    merchantList,
    countriesList,
    isEdit,
    editFromPermission,
    productsKeyList,
    warnMessage,
    onCountryFilterEdit,
    onSelectMerchant,
  };
}
